import './index.css';
import './navbarStyles.css';
import { useEffect, useState, useRef } from 'react';
import Footer from './Footer';
import { FiMenu, FiX } from 'react-icons/fi';
import ParticleBackground from './ParticleBackground';
import PartnerSlider from './PartnerSlider';
import ScrollAnimation from './ScrollAnimation';
import StaggerItem from './StaggerItem';
import { motion } from 'framer-motion';
import AnimatedNavbar from './AnimatedNavbar';

function App() {
  return (
    <div className="bg-black text-white min-h-screen">
      {/* Animated Navbar */}
      <AnimatedNavbar />

      {/* Hero Section with Particle Background */}
      <header className="relative h-[60vh] sm:h-[70vh] md:h-[80vh] flex items-center justify-center overflow-hidden">
        {/* Particle Background */}
        <div className="absolute inset-0 z-0 overflow-hidden bg-black">
          <ParticleBackground />
        </div>
        
        {/* Shadow Overlay */}
        <div className="absolute inset-0 bg-black/40 z-1"></div>
        
        {/* Additional Shadow from Bottom */}
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black to-transparent z-2"></div>
        
        {/* Text Overlay with Animations */}
        <div className="relative z-10 text-center max-w-xs sm:max-w-sm md:max-w-lg lg:max-w-2xl px-4 sm:px-6">
          <motion.div 
            initial={{ opacity: 0, scale: 0.8 }} 
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="flex justify-center items-center space-x-2 py-4"
          >
            <img
              src="/Assets/WEBSITE LOGO WMMN-1.png"
              alt="Logo"
              className="h-16 sm:h-20 md:h-24 lg:h-36 w-auto"
            />
          </motion.div>

          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-base sm:text-lg md:text-xl neon-outline mb-6 sm:mb-8"
          >
            Connectivity without compromise. Engineered for profit reinvigoration, and stability.
          </motion.p>
          
          <motion.a
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            href="#services"
            className="inline-block bg-gradient-to-r from-gray-500 to-gray-700 text-white px-4 sm:px-6 md:px-8 py-2 md:py-3 rounded-full text-sm sm:text-base font-semibold hover:from-gray-400 hover:to-gray-600 transition duration-300 shadow-lg"
          >
            Services
          </motion.a>
        </div>
      </header>

      {/* Partner Logos Slider */}
      <PartnerSlider />

      {/* Services Section */}
      <section id="services" className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16 md:py-20 grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 md:gap-10">
        {/* Data */}
        <ScrollAnimation animation="slideInRight" delay={0.3}>
          <a
            href="https://worldmobile.io"
            target="_blank"
            rel="noopener noreferrer"
            className="block"
          >
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-5 sm:p-8 text-center hover:shadow-xl transition-all cursor-pointer relative overflow-hidden">
              {/* Watermark Logo */}
              <div className="absolute inset-0 opacity-5 z-0">
                <img src="/Assets/worldmobile.png" alt="" className="w-full h-full object-cover opacity-50" />
              </div>
              {/* Content */}
              <div className="relative z-10">
                <img src="/Assets/worldmobile.png" alt="Data" className="w-20 sm:w-24 h-20 sm:h-24 mb-5 sm:mb-7 mx-auto" />
                <h2 className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4">World Mobile</h2>
                <div className="inline-block bg-gray-600 text-white px-5 sm:px-7 py-3 sm:py-4 rounded-full text-base font-semibold hover:bg-[#fff533] hover:text-black transition shadow-md">
                  Data & Connectivity
                </div>
              </div>
            </div>
          </a>
        </ScrollAnimation>

                {/* Voice */}
        <ScrollAnimation animation="slideInLeft" delay={0.1}>
          <a
            href="https://minutesnetwork.io"
            target="_blank"
            rel="noopener noreferrer"
            className="block"
          >
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-5 sm:p-8 text-center hover:shadow-xl transition-all cursor-pointer relative overflow-hidden">
              {/* Watermark Logo */}
              <div className="absolute inset-0 opacity-5 z-0">
                <img src="/Assets/image 1566.svg" alt="" className="w-full h-full object-cover opacity-50" />
              </div>
              {/* Content */}
              <div className="relative z-10">
                <img src="/Assets/image 1566.svg" alt="Voice" className="w-20 sm:w-24 h-20 sm:h-24 mb-5 sm:mb-7 mx-auto" />
                <h2 className="text-xl sm:text-2xl font-semibold mb-3 sm:mb-4">Minutes Network</h2>
                <div className="inline-block bg-gray-600 text-white px-5 sm:px-7 py-3 sm:py-4 rounded-full text-base font-semibold hover:[background:linear-gradient(45deg,_#ff00e6,_#6e00ff)] transition shadow-md">
                  Voice & SMS
                </div>
              </div>
            </div>
          </a>
        </ScrollAnimation>
      </section>

      {/* About Section */}
      <section id="about" className="px-4 sm:px-6 py-12 sm:py-16 md:py-20 bg-[#0a0a0a] text-center overflow-hidden">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
        >
          <motion.h2 
            initial={{ opacity: 0, x: 100 }} 
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ 
              duration: 0.8, 
              type: "spring",
              stiffness: 50,
              damping: 10
            }}
            className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6"
          >
            About WMMN
          </motion.h2>
          
          <motion.p 
            initial={{ opacity: 0, x: 100 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ 
              duration: 1,
              delay: 0.2,
              type: "spring",
              stiffness: 50,
              damping: 10
            }}
            className="text-gray-400 max-w-3xl mx-auto text-base sm:text-lg leading-relaxed px-4"
          >
            WMMN is a wholesale telecommunications carrier delivering secure, high-quality international voice connectivity. Formed in 2025 as a joint venture between World Mobile Group Ltd and Minutes Network Ltd, WMMN combines Tier-1 interconnects, managed call centre traffic, Al-powered switching, and realtime fraud prevention to protect networks, improve call quality, and optimise carrier revenues across ( Africa, Asia-Pacific, and the Middle East).
          </motion.p>
        </motion.div>
      </section>

      {/* Footer */}
      <ScrollAnimation animation="slideUp" delay={0.1}>
        <Footer/>
      </ScrollAnimation>
    </div>
  );
}

export default App;
